/**
 * Dialog component inspired by shadcn/ui
 * Uses native HTML <dialog> element instead of Radix UI
 * @see https://ui.shadcn.com/docs/components/dialog
 */
import { LitheElement, LitheFC } from '@wheelbox/lithejs-engine';
import { XIcon } from '@wheelbox/lithejs-lucide';

import { createId, clsx } from '../../utils';

// ============================================================================
// Dialog Context (simple module-level state for open/close)
// ============================================================================

const dialogRegistry = new Map<string, HTMLDialogElement>();

const registerDialog = (id: string, element: HTMLDialogElement) => {
  dialogRegistry.set(id, element);
};

const unregisterDialog = (id: string) => {
  dialogRegistry.delete(id);
};

export const openDialog = (id: string) => {
  const dialog = dialogRegistry.get(id);
  if (dialog) dialog.showModal();
};

export const closeDialog = (id: string) => {
  const dialog = dialogRegistry.get(id);
  if (dialog) dialog.close();
};

// ============================================================================
// Dialog
// ============================================================================

type DialogProps = {
  classes?: string | string[];
  open?: boolean;
  onmount?: () => void;
  onunmount?: () => void;
};

/**
 * Dialog root component - wrapper that provides context
 */
const Dialog: LitheFC<DialogProps> = (
  {
    id = createId(),
    testId = 'dialog',
    classes,
    open = false,
    onmount,
    onunmount,
  },
  ...children
): LitheElement<'div'> => {
  const onMountHandler = () => {
    if (open) {
      openDialog(`${id}-content`);
    }
    onmount?.();
  };

  return [
    'div',
    {
      id,
      'data-testid': testId,
      'data-slot': 'dialog',
      'data-state': open ? 'open' : 'closed',
      class: [...clsx(classes)],
      onmount: onMountHandler,
      onunmount,
    },
    ...children,
  ];
};

// ============================================================================
// DialogTrigger
// ============================================================================

type DialogTriggerProps = {
  dialogId: string;
  classes?: string | string[];
};

/**
 * DialogTrigger - button that opens the dialog
 */
const DialogTrigger: LitheFC<DialogTriggerProps> = (
  { id = createId(), testId = 'dialog-trigger', dialogId, classes },
  ...children
): LitheElement<'button'> => [
  'button',
  {
    id,
    'data-testid': testId,
    'data-slot': 'dialog-trigger',
    type: 'button',
    class: [...clsx(classes)],
    onclick: () => openDialog(`${dialogId}-content`),
  },
  ...children,
];

// ============================================================================
// DialogContent
// ============================================================================

type DialogContentProps = {
  dialogId: string;
  classes?: string | string[];
  onClose?: () => void;
};

/**
 * DialogContent - the modal content using native <dialog>
 * Note: The `id` prop is used as the dialog's registration ID in the registry
 */
const DialogContent: LitheFC<DialogContentProps> = (
  { id = createId(), testId = 'dialog-content', dialogId, classes, onClose },
  ...children
): LitheElement<'dialog'> => {
  // Use dialogId for registry coordination, but id is required by LitheFC
  const contentId = dialogId ? `${dialogId}-content` : id;

  const onMountHandler = () => {
    const dialog = document.getElementById(contentId) as HTMLDialogElement;
    if (dialog) {
      registerDialog(contentId, dialog);

      // Close on backdrop click
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) {
          dialog.close();
        }
      });

      // Handle close event
      dialog.addEventListener('close', () => {
        onClose?.();
      });
    }
  };

  const onUnmountHandler = () => {
    unregisterDialog(contentId);
  };

  const handleClose = () => {
    closeDialog(contentId);
  };

  return [
    'dialog',
    {
      id: contentId,
      'data-testid': testId,
      'data-slot': 'dialog-content',
      class: [
        // Hidden by default, shown when open
        'hidden open:grid',
        // Backdrop - updated to match shadcn v4
        'backdrop:bg-black/50 backdrop:backdrop-blur-none',
        // Dialog positioning and sizing
        'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
        'z-50 w-full max-w-[calc(100%-2rem)] sm:max-w-lg',
        // Appearance
        'gap-4 border bg-background p-6 shadow-lg rounded-lg',
        // Animation
        'duration-200',
        'open:animate-in open:fade-in-0 open:zoom-in-95',
        ...clsx(classes),
      ],
      onmount: onMountHandler,
      onunmount: onUnmountHandler,
    },
    ...children,
    // Close button
    [
      'button',
      {
        id: `${contentId}-close-btn`,
        type: 'button',
        class: [
          'absolute right-4 top-4 rounded-sm opacity-70',
          'transition-opacity hover:opacity-100',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:pointer-events-none',
        ],
        onclick: handleClose,
      },
      XIcon({ classes: 'size-4' }),
      ['span', { class: 'sr-only' }, 'Close'],
    ],
  ];
};

// ============================================================================
// DialogHeader
// ============================================================================

type DialogHeaderProps = {
  classes?: string | string[];
};

/**
 * DialogHeader - container for title and description
 */
const DialogHeader: LitheFC<DialogHeaderProps> = (
  { id = createId(), testId = 'dialog-header', classes },
  ...children
): LitheElement<'div'> => [
  'div',
  {
    id,
    'data-testid': testId,
    'data-slot': 'dialog-header',
    class: [
      'flex flex-col gap-2 text-center sm:text-left pb-4',
      ...clsx(classes),
    ],
  },
  ...children,
];

// ============================================================================
// DialogFooter
// ============================================================================

type DialogFooterProps = {
  classes?: string | string[];
};

/**
 * DialogFooter - container for action buttons
 */
const DialogFooter: LitheFC<DialogFooterProps> = (
  { id = createId(), testId = 'dialog-footer', classes },
  ...children
): LitheElement<'div'> => [
  'div',
  {
    id,
    'data-testid': testId,
    'data-slot': 'dialog-footer',
    class: [
      'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-4',
      ...clsx(classes),
    ],
  },
  ...children,
];

// ============================================================================
// DialogTitle
// ============================================================================

type DialogTitleProps = {
  classes?: string | string[];
};

/**
 * DialogTitle - the dialog's title
 */
const DialogTitle: LitheFC<DialogTitleProps> = (
  { id = createId(), testId = 'dialog-title', classes },
  ...children
): LitheElement<'h2'> => [
  'h2',
  {
    id,
    'data-testid': testId,
    'data-slot': 'dialog-title',
    class: [
      'text-lg font-semibold leading-none tracking-tight',
      ...clsx(classes),
    ],
  },
  ...children,
];

// ============================================================================
// DialogDescription
// ============================================================================

type DialogDescriptionProps = {
  classes?: string | string[];
};

/**
 * DialogDescription - the dialog's description text
 */
const DialogDescription: LitheFC<DialogDescriptionProps> = (
  { id = createId(), testId = 'dialog-description', classes },
  ...children
): LitheElement<'p'> => [
  'p',
  {
    id,
    'data-testid': testId,
    'data-slot': 'dialog-description',
    class: ['text-sm text-muted-foreground', ...clsx(classes)],
  },
  ...children,
];

// ============================================================================
// DialogClose
// ============================================================================

type DialogCloseProps = {
  dialogId: string;
  classes?: string | string[];
};

/**
 * DialogClose - button that closes the dialog
 */
const DialogClose: LitheFC<DialogCloseProps> = (
  { id = createId(), testId = 'dialog-close', dialogId, classes },
  ...children
): LitheElement<'button'> => [
  'button',
  {
    id,
    'data-testid': testId,
    'data-slot': 'dialog-close',
    type: 'button',
    class: [...clsx(classes)],
    onclick: () => closeDialog(`${dialogId}-content`),
  },
  ...children,
];

// ============================================================================
// Exports
// ============================================================================

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
  type DialogProps,
  type DialogTriggerProps,
  type DialogContentProps,
  type DialogHeaderProps,
  type DialogFooterProps,
  type DialogTitleProps,
  type DialogDescriptionProps,
  type DialogCloseProps,
};

export default Dialog;
